import React, { Component, Fragment } from 'react';
import { connect } from 'react-redux';
import { debounce } from 'lodash';
import { Breadcrumb, Button, Col, Input, Modal, Row, Switch, Table, Tooltip, message } from 'antd';
import { DeleteOutlined, EditOutlined, EyeOutlined, PlusOutlined, SearchOutlined, CloseCircleFilled } from '@ant-design/icons';
import DatePicker from 'react-modern-calendar-datepicker';
import moment from "moment-jalaali";
import { SetRoute } from '../scripts/action';
import { CheckPermission } from '../scripts/helpers';
import { Constants, Permissions } from '../scripts/settings';
import API from '../scripts/api';
import Resources from '../scripts/resources';
import PaginationInfo from '../components/PaginationInfo';
import SpecialOfferForm from '../forms/SpecialOffer';
import SpecialOfferView from '../views/SpecialOffer';

class SpecialOffersPage extends Component {
    constructor() {
        super();

        this.state = {
            data: [],
            filter: [],
            pagination: {
                total: 0,
                current: 1,
                pageSize: Constants.pageSize,
                size: 'small'
            },
            selected: {}
        };

        this.formRef = React.createRef();

        this.handleFilter = debounce(this.handleFilter, 1000);
    }

    fetch = (params = {}) => {
        const { filterEndDate, filterSearch, filterStartDate } = this.state;

        params.q = filterSearch;
        params.endDate = filterEndDate ? moment(`${filterEndDate.year}-${filterEndDate.month}-${filterEndDate.day}`, Constants.jDateFormat).format(Constants.dateFormat) : null;
        params.startDate = filterStartDate ? moment(`${filterStartDate.year}-${filterStartDate.month}-${filterStartDate.day}`, Constants.jDateFormat).format(Constants.dateFormat) : null;

        this.setState({ loading: true });

        API.offer.list(params)
            .then((result) => {
                if (result.data) {
                    this.setState({
                        loading: false,
                        data: result.data.data,
                        pagination: {
                            ...this.state.pagination, ...{
                                current: result.data.current_page,
                                pageSize: result.data.per_page,
                                total: result.data.total
                            }
                        }
                    });
                } else {
                    this.setState({ loading: false });
                }
            })
            .catch(() => {
                this.setState({ loading: false });
            });
    }

    showModal = (offer = {}) => {
        this.setState({ visibleModal: true, selected: offer });

        setTimeout(() => {
            this.formRef.current.handleDidMount(offer);
        }, 0);
    }

    hideModal = () => {
        this.setState({ visibleModal: false });

        setTimeout(() => {
            this.formRef.current.handleWillUnmount();
        }, 0);
    }

    showDrawer = (offer = {}) => {
        this.setState({ visibleDrawer: true, selected: offer });
    }

    hideDrawer = () => {
        this.setState({ visibleDrawer: false });
    }

    handleFilter = (value, obj) => {
        const state = {};
        state[obj] = value;

        this.setState(state);

        setTimeout(() => {
            this.fetch({ page: 1 });
        }, 0);
    }

    handleStatus = (record) => {
        Modal.confirm({
            title: record.status ? Resources.confirmOfferDeactivate : Resources.confirmOfferActivate,
            okText: Resources.yes,
            okType: record.status ? 'danger' : 'primary',
            cancelText: Resources.no,
            onOk: () => {
                this.setState({ loading: true });

                API.offer.statusUpdate(record)
                    .then((result) => {
                        message.success(result.message);
                        this.fetch({ page: this.state.pagination.current });
                    })
                    .catch(() => {
                        this.setState({ loading: false });
                    });
            }
        });
    }

    handleDelete = (record) => {
        Modal.confirm({
            title: Resources.confirmDeleteOffer,
            okText: Resources.yes,
            okType: 'danger',
            cancelText: Resources.no,
            onOk: () => {
                this.setState({ loading: true });

                API.offer.delete(record)
                    .then((result) => {
                        message.success(result.message);

                        this.fetch({ page: this.state.pagination.current });
                    })
                    .catch(() => {
                        this.setState({ loading: false });
                    });
            }
        });
    }

    handleSubmit = (values) => {
        const action = this.state.selected.id ? API.offer.update : API.offer.add;

        this.setState({ submitting: true });

        action(values, this.state.selected.id)
            .then((result) => {
                message.success(result.message);
                this.setState({ submitting: false });
                this.hideModal();
                this.fetch({ page: this.state.pagination.current });
            })
            .catch(() => {
                this.setState({ submitting: false });
            });
    }

    handleTableChange = (pagination) => {
        this.fetch({ page: pagination.current });
    }

    componentDidMount() {
        const { onSetRoute, routeName, parentName } = this.props;

        onSetRoute({ openKey: parentName, selectedKey: routeName });

        this.fetch({ page: 1 });
    }

    render() {
        const { data, filterEndDate, filterStartDate, loading, pagination, selected, submitting, visibleDrawer, visibleModal } = this.state;
        const { permissions } = this.props;

        const columns = [{
            title: `${Resources.count} ${Resources.product}`,
            dataIndex: 'product',
            className: 'col-center',
            width: '120px',
            render: val => val.length
        }, {
            title: Resources.startDate,
            dataIndex: 'startDate',
            className: 'col-center',
            render: val => moment(val).format(Constants.jDateFormat)
        }, {
            title: Resources.endDate,
            dataIndex: 'endDate',
            className: 'col-center',
            render: val => moment(val).format(Constants.jDateFormat)
        }, {
            title: Resources.status,
            dataIndex: 'status',
            className: 'col-center',
            width: '70px',
            render: (val, record) => <Switch size="small" checked={!!parseInt(val)} onClick={() => this.handleStatus(record)} disabled={!permissions.action} />
        }];

        if (permissions.action) {
            columns.push({
                title: Resources.action,
                dataIndex: 'id',
                className: 'col-center',
                width: '120px',
                render: (val, record) => (
                    <Fragment>
                        <Tooltip title={Resources.view}>
                            <Button type="link" icon={<EyeOutlined />} onClick={() => this.showDrawer(record)} />
                        </Tooltip>
                        <Tooltip title={Resources.edit}>
                            <Button type="link" icon={<EditOutlined />} onClick={() => this.showModal(record)} />
                        </Tooltip>
                        <Tooltip title={Resources.delete}>
                            <Button type="link" icon={<DeleteOutlined />} onClick={() => this.handleDelete(record)} />
                        </Tooltip>
                    </Fragment>
                )
            });
        }

        const DatePickerInput = ({ ref }, date, placeholder) => (
            <Input value={date ? `${date.year}-${date.month}-${date.day}` : date} placeholder={placeholder} ref={ref} readOnly />
        );

        return (
            <Fragment>
                <Breadcrumb>
                    <Breadcrumb.Item>{Resources.specialOffers}</Breadcrumb.Item>
                </Breadcrumb>
                <div className="ant-content">
                    <Row gutter={24}>
                        {permissions.add && <Col xs={24} md={4}>
                            <Button type="primary" icon={<PlusOutlined />} onClick={() => this.showModal()} block>{Resources.add}</Button>
                        </Col>}
                        <Col xs={24} md={4}>
                            <DatePicker
                                locale="fa"
                                value={filterStartDate}
                                onChange={value => this.handleFilter(value, 'filterStartDate')}
                                renderInput={(ref => DatePickerInput(ref, filterStartDate, Resources.fromDate))}
                                shouldHighlightWeekends
                            />
                            {filterStartDate && <CloseCircleFilled className="ant-input-clear-icon ant-input-clear-icon-external" onClick={() => this.handleFilter(null, 'filterStartDate')} />}
                        </Col>
                        <Col xs={24} md={4}>
                            <DatePicker
                                locale="fa"
                                value={filterEndDate}
                                onChange={value => this.handleFilter(value, 'filterEndDate')}
                                renderInput={(ref => DatePickerInput(ref, filterEndDate, Resources.toDate))}
                                shouldHighlightWeekends
                            />
                            {filterEndDate && <CloseCircleFilled className="ant-input-clear-icon ant-input-clear-icon-external" onClick={() => this.handleFilter(null, 'filterEndDate')} />}
                        </Col>
                        <Col xs={24} md={{ span: 6, offset: permissions.add ? 6 : 10 }}>
                            <Tooltip title={Resources.searchHintOffer}>
                                <Input
                                    type="text"
                                    prefix={<SearchOutlined />}
                                    placeholder={Resources.search}
                                    onChange={({ target: { value } }) => this.handleFilter(value, 'filterSearch')}
                                    allowClear
                                />
                            </Tooltip>
                        </Col>
                    </Row>
                </div>
                <div className="ant-content">
                    <Table
                        columns={columns}
                        dataSource={data}
                        loading={loading}
                        onChange={this.handleTableChange}
                        pagination={pagination}
                        size="small"
                        rowKey="id"
                        scroll={{ x: 600 }}
                    />
                    {!!pagination.total && <PaginationInfo current={pagination.current} pageSize={pagination.pageSize} total={pagination.total} />}
                </div>
                <SpecialOfferForm ref={this.formRef} onCancel={this.hideModal} onSubmit={this.handleSubmit} submitting={submitting} visible={visibleModal} />
                <SpecialOfferView data={selected} visible={visibleDrawer} onClose={this.hideDrawer} />
            </Fragment>
        );
    }
};

export default connect(
    (state) => {
        return {
            permissions: {
                action: CheckPermission(state.app.user, Permissions.special_option),
                add: CheckPermission(state.app.user, Permissions.special_store)
            }
        };
    },
    (dispatch) => {
        return {
            onSetRoute: (data) => dispatch(SetRoute(data))
        };
    }
)(SpecialOffersPage);